/** Calendar header actions share creation, scoped filters and panel toggles
 *  across grid and Tasks states, using the existing window-event bridges. */

import {
  CalendarClock,
  Plus,
  CheckSquare,
  ListPlus,
  CalendarPlus,
  CheckCircle2,
  Flag,
  Trash2,
  Check,
  Download,
  MoreHorizontal,
} from '@/shared/ui/icons';
import type { ProjectDeadlineKind } from '../../../types/calendar';
import {
  DEADLINE_KIND_ORDER,
  DEADLINE_KIND_FILTER_LABEL,
  toggleHiddenDeadlineKind,
} from '../../../utils/projectDeadlines';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';

export interface CalendarHeaderState {
  projectScoped?: boolean;
  view: 'agenda' | 'day' | 'week' | 'month';
  headerLabel: string;
  dateIso: string;
  /** Union of overdue, at-risk and conflicted task IDs, counted once per task. */
  attentionCount?: number;
  /** Absent when the workspace has no projects, so there is nothing to filter. */
  deadlineFilter?: {
    hiddenKinds: ProjectDeadlineKind[];
    /** Flags per kind in the visible range, counted before hiding. */
    countsByKind: Record<ProjectDeadlineKind, number>;
  };
}

export const CALENDAR_SET_DEADLINE_FILTER_EVENT = 'calendar:set-deadline-filter';
export const CALENDAR_EXPORT_DEADLINES_EVENT = 'calendar:export-deadlines';

function setHiddenDeadlineKinds(hiddenKinds: ProjectDeadlineKind[]): void {
  window.dispatchEvent(new CustomEvent(CALENDAR_SET_DEADLINE_FILTER_EVENT, { detail: { hiddenKinds } }));
}

/** Per-kind visibility for the read-only project deadline flags. A tick means
 *  shown. The menu stays open between picks so several kinds can be switched
 *  in one visit. */
function buildDeadlineOptions(ctx: CalendarHeaderCtx): MobileHeaderSubAction[] {
  const filter = ctx.calendarHeaderState?.deadlineFilter;
  if (!filter) return [];
  const hidden = filter.hiddenKinds;
  const subActions: MobileHeaderSubAction[] = [
    {
      id: 'calendar-deadlines-show-all',
      label: 'Show all deadlines',
      disabled: hidden.length === 0,
      keepsMenuPresented: true,
      onClick: () => setHiddenDeadlineKinds([]),
    },
    ...DEADLINE_KIND_ORDER.map((kind): MobileHeaderSubAction => {
      const shown = !hidden.includes(kind);
      // A zero says nothing a bare label does not, and just adds noise.
      const count = filter.countsByKind[kind];
      const name = DEADLINE_KIND_FILTER_LABEL[kind];
      // ariaLabel replaces the visible label, so the count is repeated there
      // or screen readers never hear it.
      return {
        id: `calendar-deadlines-${kind}`,
        label: count > 0 ? `${name} (${count})` : name,
        ariaLabel: `${shown ? 'Hide' : 'Show'} ${name.toLowerCase()}${count > 0 ? `, ${count} in view` : ''}`,
        active: shown,
        trailingIcon: shown ? Check : undefined,
        keepsMenuPresented: true,
        onClick: () => setHiddenDeadlineKinds(toggleHiddenDeadlineKind(hidden, kind)),
      };
    }),
    {
      id: 'calendar-deadlines-export',
      label: 'Download shown flags (.ics)',
      icon: Download,
      // Nothing to write when every kind in view is hidden or the range is empty.
      disabled: DEADLINE_KIND_ORDER.every(kind => hidden.includes(kind) || filter.countsByKind[kind] === 0),
      onClick: () => window.dispatchEvent(new CustomEvent(CALENDAR_EXPORT_DEADLINES_EVENT)),
    },
  ];
  return subActions;
}

/** Published by TasksView while rows are ticked; bulk actions replace the
 *  task filters while the Calendar controls remain available. */
export interface TasksSelectionState {
  count: number;
  canDelete: boolean;
}

export interface CalendarHeaderCtx {
  materialMobile?: boolean;
  /** Native macOS/Android own the canvas toolbar, so creation stays global. */
  nativeToolbar?: boolean;
  /** Keep primary controls labelled while reserving room for the date range. */
  desktopLabelMode?: 'all' | 'primary' | 'icons';
  calendarHeaderState: CalendarHeaderState | null;
  tasksSelection?: TasksSelectionState | null;
  calendarOverdueCount: number;
  taskQueueOpen?: boolean;
  onToggleTaskQueue?: () => void;
  bookingPanelOpen?: boolean;
  onToggleBooking?: () => void;
  onNavigate?: (view: string) => void;
  pushNotificationsAction: () => void;
}


export const CALENDAR_VIEW_OPTIONS = [
  { id: 'agenda', label: 'Agenda' },
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
] as const;

/** One creation command contract for the canvas and native global toolbar. */
export function buildCalendarCreateAction(showLabel = true): MobileHeaderAction {
  const createSubActions: MobileHeaderSubAction[] = [
    {
      id: 'create-event',
      icon: CalendarPlus,
      label: 'New event',
      onClick: () => window.dispatchEvent(new CustomEvent('calendar:create-event')),
    },
    {
      id: 'create-task',
      icon: ListPlus,
      label: 'New task',
      onClick: () => window.dispatchEvent(new CustomEvent('calendar:create-task')),
    },
  ];
  return {
    id: 'calendar-create',
    icon: Plus,
    desktopLabel: showLabel ? 'Create' : undefined,
    ariaLabel: 'Create event or task',
    group: 'create',
    subActions: createSubActions,
  };
}

export function buildCalendarHeaderActions(ctx: CalendarHeaderCtx): {
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
} {
  const leadingActions: MobileHeaderAction[] = [];
  const trailingActions: MobileHeaderAction[] = [];

  // Task filters stay reachable in the Inspector header at every viewport width.
  const selection = ctx.tasksSelection;
  if (ctx.nativeToolbar && !ctx.calendarHeaderState?.projectScoped) {
    trailingActions.push(buildCalendarCreateAction(ctx.desktopLabelMode !== 'icons'));
  }
  const attentionCount = ctx.calendarHeaderState?.attentionCount ?? ctx.calendarOverdueCount;
  // Opens the existing Tasks inspector beside the grid (/calendar/tasks).
  trailingActions.push({
    id: 'task-queue',
    icon: CheckSquare,
    ariaLabel: attentionCount > 0 ? `Tasks, ${attentionCount} need attention` : 'Tasks',
    onClick: () => ctx.onToggleTaskQueue?.(),
    active: !!ctx.taskQueueOpen,
    badge: attentionCount,
    group: 'inspector',
  });

  const deadlineOptions = buildDeadlineOptions(ctx);
  const hiddenKinds = ctx.calendarHeaderState?.deadlineFilter?.hiddenKinds ?? [];
  trailingActions.push({
    id: 'calendar-options',
    icon: MoreHorizontal,
    ariaLabel: hiddenKinds.length > 0
      ? `Calendar options, ${DEADLINE_KIND_ORDER.length - hiddenKinds.length} of ${DEADLINE_KIND_ORDER.length} project deadline kinds shown`
      : 'Calendar options',
    active: !!ctx.bookingPanelOpen || hiddenKinds.length > 0,
    group: 'view',
    subActions: [
      {
        id: 'calendar-scheduler',
        icon: CalendarClock,
        label: 'Booking link',
        ariaLabel: 'Share scheduling link',
        active: !!ctx.bookingPanelOpen,
        trailingIcon: ctx.bookingPanelOpen ? Check : undefined,
        onClick: () => ctx.onToggleBooking?.(),
      },
      ...deadlineOptions,
    ],
    subActionDividerAfter: deadlineOptions.length > 0 ? 'calendar-scheduler' : undefined,
  });

  if (ctx.taskQueueOpen) {
    // A selection replaces only task filters; creation and project deadlines stay.
    if (selection && selection.count > 0) {
      trailingActions.push({
        id: 'tasks-clear-selection',
        label: 'Clear',
        badge: selection.count,
        ariaLabel: `Clear selection of ${selection.count}`,
        onClick: () => window.dispatchEvent(new CustomEvent('tasks:clear-selection')),
        group: 'view',
      });
      trailingActions.push({
        id: 'tasks-bulk-complete',
        icon: CheckCircle2,
        ariaLabel: 'Mark selected complete',
        onClick: () => window.dispatchEvent(new CustomEvent('tasks:bulk-complete')),
        group: 'modify',
      });
      trailingActions.push({
        id: 'tasks-bulk-in-progress',
        label: 'In progress',
        ariaLabel: 'Mark selected in progress',
        onClick: () => window.dispatchEvent(new CustomEvent('tasks:bulk-in-progress')),
        group: 'modify',
      });
      trailingActions.push({
        id: 'tasks-bulk-priority',
        icon: Flag,
        ariaLabel: 'Set priority of selected',
        group: 'modify',
        subActions: (['urgent', 'high', 'medium', 'low'] as const).map((priority) => ({
          id: `tasks-bulk-priority-${priority}`,
          label: priority.charAt(0).toUpperCase() + priority.slice(1),
          onClick: () => window.dispatchEvent(new CustomEvent('tasks:bulk-priority', { detail: { priority } })),
        })),
      });
      if (selection.canDelete) {
        trailingActions.push({
          id: 'tasks-bulk-delete',
          icon: Trash2,
          ariaLabel: 'Delete selected',
          onClick: () => window.dispatchEvent(new CustomEvent('tasks:bulk-delete')),
          group: 'modify',
        });
      }
    }
  }

  ctx.pushNotificationsAction();

  if (ctx.materialMobile) {
    // Creation and Tasks stay first; date navigation belongs to the canvas toolbar.
    const mobileOrder = ['calendar-create', 'task-queue'];
    const mobileActions = [...leadingActions, ...trailingActions];
    const priorityActions = mobileOrder.flatMap(id => mobileActions.filter(action => action.id === id));
    const remainingActions = mobileActions.filter(action => !mobileOrder.includes(action.id));
    return { leadingActions: [], trailingActions: [...priorityActions, ...remainingActions] };
  }

  return { leadingActions, trailingActions };
}
